/**
 * Session Management Service
 * Handles inactivity tracking, timeout warnings, and auto-logout
 * Adapted from hcp-admin-frontend for Manager Dashboard
 */

interface SessionConfig {
  idleTimeoutMinutes: number;
  warningTimeMinutes: number;
  checkIntervalSeconds: number;
}

interface ManagerTokenData {
  token: string;
  refreshToken: string;
  loginTime: number;
  lastActivity: number;
  expiresIn: number;
  idleTimeout: number;
  warningTimeout: number;
}

class SessionManager {
  private config: SessionConfig;
  private lastActivity: number;
  private warningShown: boolean = false;
  private checkInterval: NodeJS.Timeout | null = null;
  private activityListeners: Array<() => void> = [];
  private onWarningCallback?: (remainingMinutes: number) => void;
  private onExpiredCallback?: () => void;

  constructor(config: Partial<SessionConfig> = {}) {
    this.config = {
      idleTimeoutMinutes: 60,
      warningTimeMinutes: 5,
      checkIntervalSeconds: 30,
      ...config
    };

    this.lastActivity = Date.now();
    this.init();
  }

  private getCheckInterval(): number {
    const runtimeCheckInterval = localStorage.getItem('RUNTIME_SESSION_CHECK_INTERVAL_SECONDS');
    const checkIntervalSeconds = runtimeCheckInterval ? parseInt(runtimeCheckInterval) : this.config.checkIntervalSeconds;
    return checkIntervalSeconds * 1000;
  }

  private init() {
    this.setupActivityTracking();
    console.log('[SessionManager] Initialized (session checks will start after login)');
  }

  /**
   * Set session configuration from backend auth response
   */
  setSessionConfig(idleTimeoutSeconds: number, warningTimeoutSeconds: number) {
    this.config.idleTimeoutMinutes = idleTimeoutSeconds / 60;
    this.config.warningTimeMinutes = warningTimeoutSeconds / 60;

    console.log('[SessionManager] Session timeout configured:', {
      idleTimeout: `${this.config.idleTimeoutMinutes} minutes`,
      warningTime: `${this.config.warningTimeMinutes} minutes`
    });

    if (!this.checkInterval) {
      this.startSessionCheck();
    }
  }

  private setupActivityTracking() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const updateActivity = () => {
      this.updateActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
      this.activityListeners.push(() => {
        document.removeEventListener(event, updateActivity, true);
      });
    });
  }

  private updateActivity() {
    this.lastActivity = Date.now();
    this.warningShown = false;
  }

  private startSessionCheck() {
    const interval = this.getCheckInterval();
    console.log('[SessionManager] Starting session check with interval:', interval / 1000, 'seconds');
    this.checkInterval = setInterval(() => {
      this.checkSession();
    }, interval);
  }

  private checkSession() {
    if (!this.isManagerLoggedIn()) {
      return;
    }

    const now = Date.now();
    const inactiveMinutes = (now - this.lastActivity) / (1000 * 60);
    const timeoutMinutes = this.config.idleTimeoutMinutes;
    const warningMinutes = this.config.warningTimeMinutes;
    const remainingMinutes = timeoutMinutes - inactiveMinutes;

    // Check if session should expire
    if (inactiveMinutes >= timeoutMinutes) {
      console.warn(`[SessionManager] Idle timeout reached (${inactiveMinutes.toFixed(1)} minutes inactive)`);
      this.handleSessionExpired();
      return;
    }

    // Check if warning should be shown
    if (remainingMinutes <= warningMinutes && !this.warningShown) {
      this.warningShown = true;
      console.warn(`[SessionManager] Session warning: ${Math.ceil(remainingMinutes)} minutes until timeout`);
      this.handleSessionWarning(Math.ceil(remainingMinutes));
    }
  }

  private handleSessionWarning(remainingMinutes: number) {
    if (this.onWarningCallback) {
      this.onWarningCallback(remainingMinutes);
    }
  }

  private handleSessionExpired() {
    console.warn('[SessionManager] Session expired due to inactivity');
    this.cleanup();
    this.clearManagerSession();

    if (this.onExpiredCallback) {
      this.onExpiredCallback();
    }
  }

  /**
   * Get current manager token data
   */
  getManagerTokenData(): ManagerTokenData | null {
    try {
      const tokenDataStr = localStorage.getItem('managerTokenData');
      return tokenDataStr ? JSON.parse(tokenDataStr) : null;
    } catch (error) {
      console.error('Failed to parse manager token data:', error);
      return null;
    }
  }

  /**
   * Check if user is currently logged in as manager
   */
  isManagerLoggedIn(): boolean {
    const tokenData = this.getManagerTokenData();
    return tokenData !== null && !!tokenData.token;
  }

  /**
   * Clear manager session data
   */
  clearManagerSession() {
    localStorage.removeItem('managerTokenData');
    localStorage.removeItem('managerAuthenticated');
    localStorage.removeItem('managerUser');
    localStorage.removeItem('managerUserData');
    localStorage.removeItem('managerRole');
  }

  /**
   * Set callback for session warnings
   */
  onSessionWarning(callback: (remainingMinutes: number) => void) {
    this.onWarningCallback = callback;
  }

  /**
   * Set callback for session expiration
   */
  onSessionExpired(callback: () => void) {
    this.onExpiredCallback = callback;
  }

  /**
   * Manually refresh session (reset activity timer)
   */
  refreshSession() {
    this.updateActivity();
    console.log('Session manually refreshed');
  }

  /**
   * Get remaining session time in minutes
   */
  getRemainingSessionTime(): number {
    const now = Date.now();
    const inactiveMinutes = (now - this.lastActivity) / (1000 * 60);
    return Math.max(0, this.config.idleTimeoutMinutes - inactiveMinutes);
  }

  /**
   * Cleanup and stop session management
   */
  cleanup() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.activityListeners.forEach(cleanup => cleanup());
    this.activityListeners = [];

    console.log('SessionManager cleaned up');
  }

  /**
   * Restart session management
   */
  restart() {
    this.cleanup();
    this.lastActivity = Date.now();
    this.warningShown = false;
    this.init();

    if (this.isManagerLoggedIn()) {
      console.log('SessionManager restarted - manager logged in, starting session checks');
      this.startSessionCheck();
    } else {
      console.log('SessionManager restarted - no manager session');
    }
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();
export default sessionManager;
